import { Button } from '@/components/ui/button';
import { ChevronDown, LockKeyhole, Plus, Trash2, Unlock } from 'lucide-react';
import type { CourseSection } from '@/types/api/course.types';

interface CourseSectionsDrawerProps {
  isDrawerOpen: boolean;
  isLearner: boolean;
  canEdit: boolean;
  onCourseDetailsClick: () => void;
  chapterName: string;
  setChapterName: (value: string) => void;
  onAddChapter: () => void;
  isCreatingSection: boolean;
  courseSections?: CourseSection[];
  activeSectionId: number | null;
  onSelectSection: (sectionId: number) => void;
  onDeleteSection: (sectionId: number) => void;
  isDeletingSection: boolean;
  onToggleSectionLock: (sectionId: number, isActive: boolean) => void;
  isPatchingSection: boolean;
}

const CourseSectionsDrawer = ({
  isDrawerOpen,
  isLearner,
  canEdit,
  onCourseDetailsClick,
  chapterName,
  setChapterName,
  onAddChapter,
  isCreatingSection,
  courseSections,
  activeSectionId,
  onSelectSection,
  onDeleteSection,
  isDeletingSection,
  onToggleSectionLock,
  isPatchingSection,
}: CourseSectionsDrawerProps) => {
  if (!isDrawerOpen) return null;

  return (
    <aside className="flex flex-col rounded-xl border bg-gradient-to-b from-slate-100 to-slate-200 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:rounded-none lg:border-y-0 lg:border-r-0 lg:border-l dark:from-slate-900 dark:to-slate-800">
      <div className="sticky top-0 z-20 mb-2 border-b border-slate-300 bg-gradient-to-b from-slate-100 to-slate-200 px-3 pb-3 pt-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-800 lg:pt-6">
        <div className="mb-3 px-1">
          <p className="text-sm font-semibold">Chapters</p>
        </div>
        {canEdit && (
          <div className="rounded-md border overflow-hidden bg-background">
            <div className="flex items-center">
              <input
                type="text"
                value={chapterName}
                onChange={(e) => setChapterName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onAddChapter();
                  }
                }}
                placeholder="Add chapter"
                className="h-11 flex-1 bg-background px-3 text-sm outline-none"
              />
              <Button
                type="button"
                className="h-11 rounded-none px-4"
                size="icon"
                aria-label="Add chapter"
                disabled={!chapterName.trim() || isCreatingSection}
                onClick={onAddChapter}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mx-3 flex-1 overflow-hidden rounded-xl border bg-background shadow-sm">
        <button
          type="button"
          className="w-full border-b bg-muted/60 px-4 py-3 font-medium text-left hover:bg-muted/80 transition-colors"
          onClick={onCourseDetailsClick}
        >
          Course details
        </button>
        <div className="max-h-[50vh] overflow-y-auto lg:max-h-[calc(100vh-15rem)]">
          {courseSections?.map((section) => {
            const isLockedForLearner = isLearner && !section.is_active;

            return (
              <div
                key={section.id}
                className={`group flex items-center justify-between border-b px-4 py-3 cursor-pointer transition-colors ${
                  isLockedForLearner
                    ? 'opacity-45 saturate-50 cursor-not-allowed'
                    : activeSectionId === section.id
                      ? 'bg-primary/10'
                      : 'hover:bg-muted/40'
                }`}
                onClick={() => {
                  if (isLockedForLearner) return;
                  onSelectSection(section.id);
                }}
              >
              <div className="flex items-center gap-2 pr-3">
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{section.section_name}</span>
              </div>
              <div className="flex items-center gap-2">
                {canEdit && (
                  <button
                    type="button"
                    aria-label="Delete chapter"
                    title="Delete chapter"
                    className="h-6 w-6 inline-flex items-center justify-center rounded-sm text-destructive opacity-0 transition-opacity hover:bg-destructive/10 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSection(section.id);
                    }}
                    disabled={isDeletingSection}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                {canEdit ? (
                  <button
                    type="button"
                    aria-label={section.is_active ? 'Unlock chapter' : 'Lock chapter'}
                    title={section.is_active ? 'Unlock chapter' : 'Lock chapter'}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:bg-muted/70"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSectionLock(section.id, section.is_active);
                    }}
                    disabled={isPatchingSection}
                  >
                    {section.is_active ? (
                      <Unlock className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <LockKeyhole className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    )}
                  </button>
                ) : section.is_active ? (
                  <Unlock className="h-4 w-4 text-emerald-500" />
                ) : (
                  <LockKeyhole className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              </div>
            );
          })}
          {!courseSections?.length && (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No course chapters available.
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border p-4 text-xs text-muted-foreground mx-3 mt-3 bg-background shadow-sm">
        Total chapters: {courseSections?.length || 0}
      </div>
    </aside>
  );
};

export default CourseSectionsDrawer;
